/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// state
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

// datatable measurabilities
import { tableSchemaBase as columns } from '../measurabilities.columns';

// types
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';
import { TMeasurabilityProject } from '@pages/Offsets/interfaces';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IMeasurabilitiesSlice {
	/* Pagination */
	measurabilitiesTotalPages: number;
	measurabilitiesPage: number;
	measurabilitiesTotal: number;
	measurabilitiesPageSize: number;

	measurabilitiesQueryParams: TQueryParams;
	measurabilitiesSelectedRow: Partial<TMeasurabilityProject>;
	measurabilitiesSortParams: string;
	measurabilitiesSortStatus: {};
	measurabilitiesOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};

	/* Setters */
	setMeasurabilitiesPage: (_page: number) => void;
	setMeasurabilitiesPageSize: (_pageSize: number) => void;
	setMeasurabilitiesTotalPages: (
		_totalPages: number
	) => void;

	setMeasurabilitiesQueryParams: (
		_measurabilitiesQueryParams: TQueryParams
	) => void;
	setMeasurabilitiesSelectedRow: (
		_measurabilitiesSelectedRow: Partial<TMeasurabilityProject>
	) => void;
	setMeasurabilitiesSortParams: (
		_measurabilitiesSortParams: string
	) => void;

	setMeasurabilitiesSortStatus: (
		_measurabilitiesSortStatus: TSortStatus
	) => void;

	setMeasurabilitiesOpenConfirmationModalDelete: (_measurabilitiesOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

export const partializeMeasurabilitiesSlice = (
	s: any
): any => ({
	measurabilitiesOptions: s.measurabilitiesOptions,
});

const initialState = {
	measurabilitiesOptions: columns.filter(
		(item) => item.defaultVisible
	),
	measurabilitiesTotalPages: 10,
	measurabilitiesPage: 1,
	measurabilitiesTotal: 0,
	measurabilitiesPageSize: 100,

	measurabilitiesSelectedRecords: [],
	measurabilitiesQueryParams: {},
	measurabilitiesSelectedRow: {},
	measurabilitiesSortParams: '-createdAt',
	measurabilitiesSortStatus: {},
	measurabilitiesOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
};

export const createMeasurabilitiesSlice: StateCreator<
	TStore,
	[['zustand/persist', any]],
	[],
	IMeasurabilitiesSlice
> = (set) => ({
	...initialState,
	setMeasurabilitiesPage: (_page: number) =>
		set({ measurabilitiesPage: _page }),
	setMeasurabilitiesPageSize: (_pageSize: number) =>
		set({ measurabilitiesPageSize: _pageSize }),
	setMeasurabilitiesTotalPages: (_totalPages: number) =>
		set({ measurabilitiesTotalPages: _totalPages }),

	setMeasurabilitiesQueryParams: (
		_measurabilitiesQueryParams: TQueryParams
	) =>
		set({
			measurabilitiesQueryParams:
				_measurabilitiesQueryParams,
		}),
	setMeasurabilitiesSelectedRow: (
		_measurabilitiesSelectedRow: Partial<TMeasurabilityProject>
	) =>
		set({
			measurabilitiesSelectedRow:
				_measurabilitiesSelectedRow,
		}),
	setMeasurabilitiesSortParams: (
		_measurabilitiesSortParams: string
	) =>
		set({
			measurabilitiesSortParams:
				_measurabilitiesSortParams,
		}),

	setMeasurabilitiesSortStatus: (
		_sortStatus: TSortStatus
	) => set({ measurabilitiesSortStatus: _sortStatus }),

	setMeasurabilitiesOpenConfirmationModalDelete:
		(_measurabilitiesOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				measurabilitiesOpenConfirmationModalDelete:
					_measurabilitiesOpenConfirmationModalDelete,
			}),
});
